import React, { useState } from "react";
import {
  Drawer,
  List,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Collapse,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import RestaurantIcon from "@mui/icons-material/Restaurant";

export const DRAWER_WIDTH = 264;
export const DRAWER_RAIL_WIDTH = 72;

const DrawerComponent = ({
  open,
  onDrawerClose,
  menuItems,
  selectedItem,
  onItemSelect,
  role,
  ordersExpanded,
  orderPage,
  onOrderPageSelect,
  kitchenExpanded,
  kitchenPage,
  onKitchenPageSelect,
  billingExpanded,
  billingPage,
  onBillingPageSelect,
}) => {
  const fullAccess = ["super_admin", "admin", "cashier"].includes(role);
  const orderPages = [
    ...(fullAccess
      ? [
          { label: "Online Orders", value: "OrderDetail" },
          { label: "Contact Orders", value: "ContactOrder" },
          { label: "Menu Orders", value: "MakeMenuOrder" },
        ]
      : []),
    ...(fullAccess || role === "waiter"
      ? [{ label: "Restaurant", value: "WaiterTable" }]
      : []),
    ...(fullAccess || role === "rider"
      ? [{ label: "Rider", value: "RiderTable" }]
      : []),
  ];
  const activeOrderPage =
    orderPage ||
    (role === "waiter"
      ? "WaiterTable"
      : role === "rider"
      ? "RiderTable"
      : "OrderDetail");
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("md"));
  const [hovered, setHovered] = useState(false);
  const expanded = mobile || hovered;
  return (
    <Drawer
      variant={mobile ? "temporary" : "permanent"}
      open={mobile ? open : true}
      onClose={onDrawerClose}
      PaperProps={{
        onMouseEnter: () => !mobile && setHovered(true),
        onMouseLeave: () => !mobile && setHovered(false),
        onFocusCapture: () => !mobile && setHovered(true),
        onBlurCapture: (event) => {
          if (!mobile && !event.currentTarget.contains(event.relatedTarget))
            setHovered(false);
        },
      }}
      sx={{
        width: { md: DRAWER_RAIL_WIDTH },
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: expanded ? DRAWER_WIDTH : DRAWER_RAIL_WIDTH,
          transition: theme.transitions.create("width"),
          overflowX: "hidden",
          "& .MuiListItemText-root, & .MuiTypography-overline": {
            display: expanded ? undefined : "none",
          },
          boxSizing: "border-box",
          backgroundColor: "#142e29",
          color: "#f4faf7",
          border: 0,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 2.5,
          minHeight: 80,
        }}
      >
        <RestaurantIcon sx={{ color: "#a1d6bd" }} />
        <Box sx={{ flex: 1, display: expanded ? "block" : "none" }}>
          <Typography fontWeight={750}>BBG Admin</Typography>
          <Typography variant="caption" sx={{ color: "#b3c9c1" }}>
            Restaurant management
          </Typography>
        </Box>
        {mobile && (
          <IconButton
            aria-label="Close navigation"
            onClick={onDrawerClose}
            sx={{ color: "inherit" }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      <Divider sx={{ borderColor: "#ffffff18" }} />
      <Box
        component="nav"
        aria-label="Main navigation"
        sx={{ flex: 1, overflowY: "auto", px: 1.5, py: 2 }}
      >
        <Typography
          variant="overline"
          sx={{ px: 1.5, color: "#b3c9c1", letterSpacing: 1.5 }}
        >
          Workspace
        </Typography>
        <List sx={{ pt: 1 }}>
          {menuItems.map(({ text, icon }) => (
            <Box key={text}>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  aria-label={text}
                  title={!expanded ? text : undefined}
                  onClick={() => onItemSelect(text)}
                  selected={selectedItem === text}
                  aria-expanded={
                    text === "Order Management"
                      ? ordersExpanded
                      : text === "Kitchen"
                      ? kitchenExpanded
                      : text === "Billing Report"
                      ? billingExpanded
                      : undefined
                  }
                  aria-current={selectedItem === text ? "page" : undefined}
                  sx={{
                    borderRadius: 2,
                    py: 1.1,
                    px: expanded ? 2 : 1,
                    justifyContent: expanded ? "flex-start" : "center",
                    "&:hover": { bgcolor: "#ffffff0d" },
                    "&.Mui-selected": {
                      bgcolor: "#ffffff16",
                      color: "#c4f0d8",
                      "&:hover": { bgcolor: "#ffffff20" },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: expanded ? 38 : 0,
                      color: "inherit",
                      "& .MuiSvgIcon-root": { fontSize: 21 },
                    }}
                  >
                    {icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={text}
                    primaryTypographyProps={{
                      fontSize: 13,
                      fontWeight: selectedItem === text ? 700 : 500,
                    }}
                  />
                  {expanded &&
                    text === "Order Management" &&
                    (ordersExpanded ? <ExpandLess /> : <ExpandMore />)}
                  {expanded &&
                    text === "Kitchen" &&
                    (kitchenExpanded ? <ExpandLess /> : <ExpandMore />)}
                  {expanded &&
                    text === "Billing Report" &&
                    (billingExpanded ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </ListItem>
              {text === "Order Management" && (
                <Collapse in={expanded && ordersExpanded}>
                  <List disablePadding aria-label="Order Management pages">
                    {orderPages.map((page) => (
                      <ListItem key={page.value} disablePadding>
                        <ListItemButton
                          selected={
                            selectedItem === text &&
                            activeOrderPage === page.value
                          }
                          onClick={() => onOrderPageSelect(page.value)}
                          aria-current={
                            selectedItem === text &&
                            activeOrderPage === page.value
                              ? "page"
                              : undefined
                          }
                          sx={{
                            pl: 6,
                            borderRadius: 2,
                            "&.Mui-selected": {
                              bgcolor: "#ffffff16",
                              color: "#c4f0d8",
                            },
                          }}
                        >
                          <ListItemText
                            primary={page.label}
                            primaryTypographyProps={{ fontSize: 13 }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
              {text === "Kitchen" && (
                <Collapse in={expanded && kitchenExpanded}>
                  <List disablePadding aria-label="Kitchen pages">
                    {[
                      { label: "Online Orders", value: "online" },
                      { label: "Restaurant Orders", value: "restaurant" },
                    ].map((page) => (
                      <ListItem key={page.value} disablePadding>
                        <ListItemButton
                          selected={
                            selectedItem === text && kitchenPage === page.value
                          }
                          onClick={() => onKitchenPageSelect(page.value)}
                          aria-current={
                            selectedItem === text && kitchenPage === page.value
                              ? "page"
                              : undefined
                          }
                          sx={{
                            pl: 6,
                            borderRadius: 2,
                            "&.Mui-selected": {
                              bgcolor: "#ffffff16",
                              color: "#c4f0d8",
                            },
                          }}
                        >
                          <ListItemText
                            primary={page.label}
                            primaryTypographyProps={{ fontSize: 13 }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
              {text === "Billing Report" && (
                <Collapse in={expanded && billingExpanded}>
                  <List disablePadding aria-label="Billing Report pages">
                    {[
                      { label: "Transaction Report", value: "transactions" },
                      { label: "Restaurant Orders", value: "restaurant" },
                      { label: "Online Sales", value: "online" },
                    ].map((page) => (
                      <ListItem key={page.value} disablePadding>
                        <ListItemButton
                          selected={
                            selectedItem === text && billingPage === page.value
                          }
                          onClick={() => onBillingPageSelect(page.value)}
                          aria-current={
                            selectedItem === text && billingPage === page.value
                              ? "page"
                              : undefined
                          }
                          sx={{
                            pl: 6,
                            borderRadius: 2,
                            "&.Mui-selected": {
                              bgcolor: "#ffffff16",
                              color: "#c4f0d8",
                            },
                          }}
                        >
                          <ListItemText
                            primary={page.label}
                            primaryTypographyProps={{ fontSize: 13 }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </Box>
          ))}
        </List>
      </Box>
    </Drawer>
  );
};
export default DrawerComponent;
